import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, LoadingSkeleton, ToastContainer } from './ui';
import { safeErrorMessage } from '../utils/apiError';

function BulkEnroll({ token, embedded = false }) {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [targetClassId, setTargetClassId] = useState('');
  const [unassignedOnly, setUnassignedOnly] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success') => setToasts(prev => [...prev, { message, type }]);
  const removeToast = (index) => setToasts(prev => prev.filter((_, i) => i !== index));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [classesRes, studentsRes] = await Promise.all([
        fetch('/api/v1/classes', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/students', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setClasses(await classesRes.json());
      setStudents(await studentsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const visibleStudents = unassignedOnly ? students.filter(s => !s.class_id) : students;

  const toggleStudent = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const allVisibleSelected = visibleStudents.length > 0 && visibleStudents.every(s => selectedIds.includes(s.id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(prev => prev.filter(id => !visibleStudents.some(s => s.id === id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...visibleStudents.map(s => s.id)])]);
    }
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const response = await fetch(`/api/v1/classes/${targetClassId}/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ student_ids: selectedIds }),
      });
      if (response.ok) {
        addToast(`Enrolled ${selectedIds.length} student(s)`);
        setSelectedIds([]);
        fetchData();
      } else {
        addToast(await safeErrorMessage(response, 'Failed to enroll students'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    } finally {
      setEnrolling(false);
    }
  };

  const columns = [
    {
      header: (
        <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />
      ),
      width: '40px',
      render: (row) => (
        <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleStudent(row.id)} />
      ),
    },
    { header: 'Full Name', accessor: 'full_name' },
    { header: 'National ID', render: (row) => row.national_id || '—' },
    {
      header: 'Current Class',
      render: (row) => row.class_name ? <Badge variant="default">{[row.class_name, row.stream].filter(Boolean).join(' ')}</Badge> : <Badge variant="warning">Unassigned</Badge>,
    },
  ];

  if (loading) return <LoadingSkeleton type="text" rows={10} />;

  return (
    <div>
      {!embedded && (
        <div style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Bulk Enroll Students</h2>
        </div>
      )}

      <Card>
        <div className="form-row">
          <div className="form-group">
            <label>Target Class *</label>
            <select value={targetClassId} onChange={(e) => setTargetClassId(e.target.value)}>
              <option value="">Select Class</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={unassignedOnly} onChange={(e) => setUnassignedOnly(e.target.checked)} />
              Unassigned only
            </label>
          </div>
        </div>

        <Table columns={columns} data={visibleStudents} emptyMessage="No students match this filter." />

        <div style={{ marginTop: '1rem' }}>
          <Button variant="primary" onClick={handleEnroll} disabled={!targetClassId || selectedIds.length === 0 || enrolling}>
            {enrolling ? 'Enrolling…' : `Enroll ${selectedIds.length} Selected`}
          </Button>
        </div>
      </Card>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default BulkEnroll;
